import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Plus, Trash2 } from 'lucide-react';

function EducationStep({ resume, saveResume, onNext, saving, registerPendingData }) {
  const [education, setEducation] = useState([]);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (resume?.education) {
      console.log('[EducationStep] Loading from resume');
      setEducation(resume.education);
    }
    setIsDirty(false);
  }, [resume?.education]);

  // Register pending data whenever education changes
  useEffect(() => {
    if (isDirty && registerPendingData) {
      registerPendingData({ education });
    }
  }, [education, isDirty, registerPendingData]);

  const addEducation = () => {
    const newEdu = {
      institution: '',
      degree_or_cert: '',
      start_month: 1,
      start_year: 2020,
      end_month: 1,
      end_year: 2024
    };
    setEducation([...education, newEdu]);
    setIsDirty(true);
  };

  const removeEducation = (index) => {
    setEducation(education.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const updateEducation = useCallback((index, field, value) => {
    setEducation(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setIsDirty(true);
  }, []);

  return (
    <div className="space-y-6">
      <p className="text-slate-600">Add your education.</p>

      {education.map((edu, index) => (
        <Card key={index} className="p-6 border border-slate-200" data-testid={`education-${index}`}>
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Education {index + 1}</h3>
            <Button
              data-testid={`remove-education-${index}-btn`}
              onClick={() => removeEducation(index)}
              variant="ghost"
              size="sm"
              className="text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-slate-900 font-medium">Institution</Label>
              <Input
                data-testid={`institution-input-${index}`}
                value={edu.institution}
                onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                className="h-12 mt-2"
                placeholder="University Name or Training Center"
              />
            </div>

            <div>
              <Label className="text-slate-900 font-medium">Degree or Certification</Label>
              <Input
                data-testid={`degree-input-${index}`}
                value={edu.degree_or_cert}
                onChange={(e) => updateEducation(index, 'degree_or_cert', e.target.value)}
                className="h-12 mt-2"
                placeholder="Bachelor of Science, PMP Certification, etc."
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-slate-900 font-medium">Start Month</Label>
                <Input
                  data-testid={`start-month-input-${index}`}
                  type="number"
                  min="1"
                  max="12"
                  value={edu.start_month || ''}
                  onChange={(e) => updateEducation(index, 'start_month', e.target.value ? parseInt(e.target.value) : null)}
                  className="h-12 mt-2"
                />
              </div>
              <div>
                <Label className="text-slate-900 font-medium">Start Year</Label>
                <Input
                  data-testid={`start-year-input-${index}`}
                  type="number"
                  min="1950"
                  max="2100"
                  value={edu.start_year || ''}
                  onChange={(e) => updateEducation(index, 'start_year', e.target.value ? parseInt(e.target.value) : null)}
                  className="h-12 mt-2"
                />
              </div>
              <div>
                <Label className="text-slate-900 font-medium">End Month</Label>
                <Input
                  data-testid={`end-month-input-${index}`}
                  type="number"
                  min="1"
                  max="12"
                  value={edu.end_month || ''}
                  onChange={(e) => updateEducation(index, 'end_month', e.target.value ? parseInt(e.target.value) : null)}
                  className="h-12 mt-2"
                />
              </div>
              <div>
                <Label className="text-slate-900 font-medium">End Year</Label>
                <Input
                  data-testid={`end-year-input-${index}`}
                  type="number"
                  min="1950"
                  max="2100"
                  value={edu.end_year || ''}
                  onChange={(e) => updateEducation(index, 'end_year', e.target.value ? parseInt(e.target.value) : null)}
                  className="h-12 mt-2"
                />
              </div>
            </div>
          </div>
        </Card>
      ))}

      <Button
        data-testid="add-education-btn"
        onClick={addEducation}
        variant="outline"
        className="w-full border-dashed border-2 border-slate-300 text-slate-700 hover:bg-slate-50"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Education
      </Button>
    </div>
  );
}

export default EducationStep;